import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { huHU } from '@mui/material/locale';
import { CircularProgress, Box, GlobalStyles } from '@mui/material';
import { useAuth } from './contexts/AuthContext';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import PageLoader from './components/Layout/PageLoader';
import { BOTTOM_NAV_HEIGHT, useBottomNavVisible } from './components/Layout/useBottomNavVisible';
// Eager oldalak: főoldal és belépés/regisztráció (első látogatáskor ezek kellenek azonnal)
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import { SnackbarProvider } from 'notistack';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Lusta (lazy) oldalak: külön chunkba kerülnek, csak megnyitáskor töltődnek be
const GardenList = lazyWithRetry(() => import('./pages/GardenList'));
const GardenEditor = lazyWithRetry(() => import('./pages/GardenEditor'));
const GardenDetail = lazyWithRetry(() => import('./pages/GardenDetail'));
const Plants = lazyWithRetry(() => import('./pages/Plants'));
const PlantLists = lazyWithRetry(() => import('./pages/PlantLists'));
const PlantListDetail = lazyWithRetry(() => import('./pages/PlantListDetail'));
const IndoorPlantCare = lazyWithRetry(() => import('./pages/IndoorPlantCare'));
const GardenCalendar = lazyWithRetry(() => import('./pages/GardenCalendar'));
const GardenTools = lazyWithRetry(() => import('./pages/GardenTools'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const Aszf = lazyWithRetry(() => import('./pages/Aszf'));
const AdminPanel = lazyWithRetry(() => import('./components/admin/AdminPanel'));
const PesticideFavorites = lazyWithRetry(() => import('./components/tools/PesticideFavorites'));
const PesticideComparison = lazyWithRetry(() => import('./components/tools/PesticideComparison'));
const SprayerList = lazyWithRetry(() => import('./components/tools/SprayerList'));
const PesticideDatabase = lazyWithRetry(() => import('./components/tools/PesticideDatabase'));
const NurseryDashboard = lazyWithRetry(() => import('./pages/NurseryDashboard'));
const NurseryRegistration = lazyWithRetry(() => import('./pages/NurseryRegistration'));

/** Az alsó sáv teljes magassága a biztonsági sávval (iOS home indicator) együtt. */
const BOTTOM_NAV_OFFSET = `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`;

// Védett útvonal komponens - optimalizált verzió
const ProtectedRoute: React.FC<{ element: React.ReactNode; adminOnly?: boolean }> = ({ 
  element, 
  adminOnly = false 
}) => {
  const { user, loading } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const previousAuthState = useRef({ user, loading });
  const authCheckTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Csak akkor ellenőrizzük az autentikációt, ha a felhasználói állapot változott
  useEffect(() => {
    // Ha az előző állapot már megmutatta, hogy be van jelentkezve, nem kell újra ellenőrizni
    if (previousAuthState.current.user && user) {
      setIsChecking(false);
      return;
    }
    
    // Ha még betöltés alatt van, várjunk
    if (loading) {
      return;
    }
    
    // Ha nincs user, és nem is töltődik, nem kell tovább ellenőrizni
    if (!user && !loading) {
      setIsChecking(false);
      return;
    }
    
    // Frissítjük az előző állapotot
    previousAuthState.current = { user, loading };
    
    // Ha ellenőrzés van folyamatban, töröljük a korábbi időzítőt
    if (authCheckTimeout.current) {
      clearTimeout(authCheckTimeout.current);
    }
    
    // Minimális késleltetéssel ellenőrizzük, hogy elkerüljük a többszörös ellenőrzést
    authCheckTimeout.current = setTimeout(() => {
      setIsChecking(false);
    }, 100);
    
    // Tisztítás
    return () => {
      if (authCheckTimeout.current) {
        clearTimeout(authCheckTimeout.current);
      }
    };
  }, [user, loading]);
  
  // Ha még töltődik az auth állapot, betöltő indikátort mutatunk
  if (loading) {
  return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: 'calc(100vh - 120px)' 
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Ha nincs bejelentkezve, átirányítjuk a login oldalra
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Ha admin-only útvonal és nem admin, átirányítjuk a főoldalra
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return <>{element}</>;
};

// Téma létrehozása
const theme = createTheme({
  palette: {
    primary: {
      main: '#4caf50',
    },
    secondary: {
      main: '#ff9800',
    },
  },
}, huHU);

const AppRoutes = () => {
  const bottomNavVisible = useBottomNavVisible();

  return (
    <>
      {/* viewport-fit=cover mellett fekvő módban a tartalom ne csússzon a notch alá */}
      <GlobalStyles
        styles={{
          body: {
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
          },
        }}
      />
      {bottomNavVisible && (
        <GlobalStyles
          styles={{
            // A rögzített (fixed) lebegő gombok és snackbarok ne kerüljenek az alsó sáv alá
            '.MuiFab-root': { marginBottom: BOTTOM_NAV_OFFSET },
            '.notistack-SnackbarContainer': { marginBottom: BOTTOM_NAV_OFFSET },
          }}
        />
      )}
      <Header />
      <Box sx={{ flexGrow: 1 }}>
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route 
            path="/gardens" 
            element={<ProtectedRoute element={<GardenList />} />} 
          />
          <Route 
            path="/gardens/:id" 
            element={<ProtectedRoute element={<GardenDetail />} />} 
          />
          <Route 
            path="/gardens/:id/edit" 
            element={<ProtectedRoute element={<GardenEditor />} />} 
          />
          <Route 
            path="/gardens/new" 
            element={<ProtectedRoute element={<GardenEditor />} />} 
          />
          <Route 
            path="/plants" 
            element={<ProtectedRoute element={<Plants />} />} 
          />
          <Route
            path="/plant-lists"
            element={<ProtectedRoute element={<PlantLists />} />}
          />
          <Route
            path="/indoor-plants"
            element={<ProtectedRoute element={<IndoorPlantCare />} />}
          />
          <Route
            path="/plant-lists/:id"
            element={<ProtectedRoute element={<PlantListDetail />} />}
          />
          <Route 
            path="/garden-calendar" 
            element={<ProtectedRoute element={<GardenCalendar />} />} 
          />
          <Route 
            path="/garden-tools" 
            element={<ProtectedRoute element={<GardenTools />} />} 
          />
          <Route 
            path="/pesticide-favorites" 
            element={<ProtectedRoute element={<PesticideFavorites />} />} 
          />
          <Route
            path="/pesticide-comparison"
            element={<ProtectedRoute element={<PesticideComparison />} />}
          />
          <Route
            path="/pesticide-database"
            element={<ProtectedRoute element={<PesticideDatabase />} />}
          />
          <Route
            path="/sprayer-list"
            element={<ProtectedRoute element={<SprayerList />} />}
          />
          <Route 
            path="/profile" 
            element={<ProtectedRoute element={<Profile />} />} 
          />
          <Route
            path="/admin"
            element={<ProtectedRoute element={<AdminPanel />} adminOnly={true} />}
          />
          <Route
            path="/kerteszet"
            element={<ProtectedRoute element={<NurseryDashboard />} />}
          />
          <Route
            path="/kerteszet/regisztracio"
            element={<ProtectedRoute element={<NurseryRegistration />} />}
          />
          <Route path="/aszf" element={<Aszf />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </ErrorBoundary>
      </Box>
      <Footer />
      {/* Térköz, hogy az alsó navigációs sáv ne takarja el a tartalmat és a láblécet */}
      {bottomNavVisible && (
        <Box aria-hidden data-testid="bottom-nav-spacer" sx={{ height: BOTTOM_NAV_OFFSET, flexShrink: 0 }} />
      )}
    </>
  );
};

const App: React.FC = () => {
  return (
    <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppRoutes />
      </ThemeProvider>
      <ToastContainer aria-label="Értesítések" />
    </SnackbarProvider>
  );
};

export default App;
